// Initials-based user avatar with a deterministic warm-tone background.
// Used in the consumer header (right side) and the Account page. No
// network image dependency. Email is used to derive the initial when
// no display name is available.

interface UserAvatarProps {
  name?: string | null;
  email?: string | null;
  size?: number;
}

const PALETTE = [
  { bg: '#ffd6b3', fg: '#7c2d12' },
  { bg: '#a7d7c5', fg: '#14532d' },
  { bg: '#cfe1ee', fg: '#0f4c75' },
  { bg: '#fde68a', fg: '#78350f' },
  { bg: '#fbcfe8', fg: '#831843' },
  { bg: '#ddd6fe', fg: '#3730a3' },
] as const;
const PALETTE_FALLBACK = PALETTE[0];

function hashCode(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

// Stop-list emails are placeholder accounts (test seeds, automation). Showing
// an initial like "E" for "e2e-test@..." reads to real users as broken state,
// so we render a small person glyph instead. Mirrors the firstNameFromEmail
// allowlist in hooks/useMe.ts.
function isPlaceholderEmail(email?: string | null): boolean {
  if (!email) return true;
  const local = email.split('@')[0]?.toLowerCase() ?? '';
  if (!local) return true;
  if (
    /^(admin|test|demo|user|guest|root|system|noreply|info|support|hello|contact|webmaster|null|undefined|e2e)/.test(
      local,
    )
  )
    return true;
  return false;
}

function initialsFor(name?: string | null, email?: string | null): string {
  if (name && name.trim().length > 0) {
    const parts = name.trim().split(/\s+/);
    const first = parts[0] ?? '';
    if (parts.length === 1) return first.slice(0, 2).toUpperCase();
    const last = parts[parts.length - 1] ?? '';
    return ((first[0] ?? '') + (last[0] ?? '')).toUpperCase();
  }
  if (email && !isPlaceholderEmail(email)) {
    return (email[0] ?? 'M').toUpperCase();
  }
  return '';
}

export function UserAvatar({ name, email, size = 40 }: UserAvatarProps): JSX.Element {
  const key = (email ?? name ?? 'medreview-user').toLowerCase();
  const palette = PALETTE[hashCode(key) % PALETTE.length] ?? PALETTE_FALLBACK;
  const text = initialsFor(name, email);
  return (
    <span
      className="user-avatar"
      style={{
        width: size,
        height: size,
        background: palette.bg,
        color: palette.fg,
        fontSize: Math.round(size * 0.42),
      }}
      aria-hidden="true"
    >
      {text || (
        // Fallback person glyph when there's no usable initial (placeholder
        // or empty email). Better than rendering a lone "E" that the focus
        // group consistently misread as broken state.
        <svg
          viewBox="0 0 24 24"
          width={Math.round(size * 0.55)}
          height={Math.round(size * 0.55)}
          fill="currentColor"
          role="img"
        >
          <title>Account</title>
          <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
        </svg>
      )}
    </span>
  );
}
